import { View, type ViewStyle } from 'react-native'
import { SeatAlignment } from '../Table/Table'
import { useTableLayoutPosition } from '../Table/useTableLayout'
import { ChipsStacks } from '../Chips'
import { useSoundEffect } from '@/hooks/useSoundEffect'
import { useMeasureOnResize } from '@/hooks/useMeasureOnResize'
import { useEffect, useRef } from 'react'
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming
} from 'react-native-reanimated'
import { scheduleOnRN } from 'react-native-worklets'
import { isWeb } from '../../WebWrapper'
import { usePotLayoutStore } from '../../../screens/GameContainer/TableContainer'

type AlignmentPosition = {
  position: ViewStyle
  chipLayout?: ViewStyle['flexDirection']
}
function useChipsAlignmentPosition(alignment?: SeatAlignment): AlignmentPosition {
  const layout = useTableLayoutPosition()
  switch (alignment) {
    case 'top':
      return {
        position: {
          bottom: 0,
          left: 0,
          right: 0,
          alignItems: 'center',
          transform: [
            {
              translateY: '100%'
            }
          ]
        },
        chipLayout: 'row'
      }
    case 'right':
      return {
        position: {
          top: layout.padding,
          left: 0,
          transform: [
            {
              translateX: '-100%'
            }
          ]
        },
        chipLayout: 'column'
      }
    case 'bottom':
      return {
        position: {
          top: 0,
          left: 0,
          right: 0,
          alignItems: 'center',
          transform: [
            {
              translateY: '-100%'
            }
          ]
        },
        chipLayout: 'row'
      }
    case 'left':
      return {
        position: {
          top: layout.padding,
          right: 0,
          transform: [
            {
              translateX: '100%'
            }
          ]
        },
        chipLayout: 'column'
      }
    default:
      return {
        position: {},
        chipLayout: undefined
      }
  }
}
type PlayerBetChipsProps = {
  amount: number
  bigBlind: number
  alignment?: SeatAlignment
  soundEnable?: boolean
  enablePotAnimation?: boolean
}
export const PlayerBetChips = ({
  alignment,
  amount,
  bigBlind,
  soundEnable = false,
  enablePotAnimation
}: PlayerBetChipsProps) => {
  const layout = useTableLayoutPosition()
  const alignmentPosition = useChipsAlignmentPosition(alignment)
  const chipSize = layout.chips.size
  const viewRef = useRef<View>(null)

  // Play sound once
  const hasPlayedSound = useRef(false)
  const { playSound } = useSoundEffect('pokerChips')

  useEffect(() => {
    if (soundEnable && hasPlayedSound.current === false) {
      playSound()
      hasPlayedSound.current = true
    }
  }, [soundEnable, playSound])

  const { chipsLayout, animatedStyle } = useChipToPotAnimation({
    enabled: !!enablePotAnimation
  })

  // Measure and update position on window resize
  useMeasureOnResize(viewRef, (x, y) => {
    chipsLayout.set({ x, y })
  })

  return (
    <View
      style={{
        position: 'absolute',
        zIndex: 9,
        ...alignmentPosition.position
      }}
    >
      <Animated.View
        ref={viewRef}
        onLayout={(e) => {
          let target = e.currentTarget
          if (isWeb) {
            // @ts-expect-error
            target = e.nativeEvent.target
          }
          target.measure((_x, _y, width, height, pageX, pageY) => {
            const x = pageX + width * 0.5
            const y = pageY + height * 0.5
            chipsLayout.set({
              x,
              y
            })
          })
        }}
        style={animatedStyle}
      >
        <ChipsStacks
          amount={amount}
          blind={bigBlind}
          // options={{
          //   order: 'desc',
          //   sort: 'count'
          // }}
          containerStyle={{
            flexDirection: alignmentPosition.chipLayout
          }}
          chipProps={{
            width: chipSize,
            height: chipSize
          }}
          chipContainerStyle={{
            borderRadius: '100%',
            shadowColor: '#000',
            shadowOffset: {
              width: 0,
              height: 2
            },
            shadowOpacity: 0.3,
            shadowRadius: 2,

            elevation: 5
          }}
        />
      </Animated.View>
    </View>
  )
}

type UseChipToPotAnimation = {
  enabled: boolean
  onComplete?: () => void
}
const useChipToPotAnimation = ({ enabled, onComplete }: UseChipToPotAnimation) => {
  //
  const chipToPotSoundRef = useRef<boolean>(false)
  const enableAnimation = useSharedValue(0)
  const { position: potLayout } = usePotLayoutStore()
  const chipsLayout = useSharedValue({ x: 0, y: 0 })
  // const { playSound } = useSoundEffect('pokerChips')

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (enabled) {
      enableAnimation.value = withSequence(
        withTiming(
          1,
          {
            duration: 600,
            easing: Easing.bezier(0.25, 0.1, 0.25, 1)
          },
          (finished) => {
            if (finished && chipToPotSoundRef.current === false) {
              chipToPotSoundRef.current = true
              // scheduleOnRN(playSound)
            }
          }
        ),
        withTiming(
          2,
          {
            duration: 200,
            easing: Easing.out(Easing.ease)
          },
          (finished) => {
            if (finished && onComplete) {
              scheduleOnRN(onComplete)
            }
          }
        )
      )
    } else {
      // NOTE: DEV ONLY, no need for the chips to go back to the original position
      enableAnimation.value = 0
    }
  }, [enabled, onComplete])

  const animatedStyle = useAnimatedStyle(() => {
    const p1 = chipsLayout.get()
    const p2 = potLayout
    const translateX = p2.x - p1.x
    const translateY = p2.y - p1.y

    const x = interpolate(enableAnimation.value, [0, 1, 1], [0, translateX])
    const y = interpolate(enableAnimation.value, [0, 1, 1], [0, translateY])
    const opacity = interpolate(enableAnimation.value, [1, 2], [1, 0])

    return {
      transform: [
        {
          translateX: x
        },
        {
          translateY: y
        }
      ],
      opacity: opacity
    }
  }, [potLayout, chipsLayout])

  return {
    animatedStyle,
    chipsLayout
  }
}
